import React, { CSSProperties } from 'react';
import { Box, Button } from '@mui/material';
import CustomTextField from '../theme-elements/CustomTextField';
import CustomFormLabel from '../theme-elements/CustomFormLabel';
import ParentCard from '../../shared/ParentCard';
import axios from 'axios';
import { FileUploader } from 'react-drag-drop-files';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useSnackbar } from 'notistack';

// import Spinner from '../../../views/spinner/Spinner';
import './Dropzone.css';
import { useLocation, useNavigate } from 'react-router';
import { links } from 'src/helpers/links';
import { useAuthChecker } from 'src/helpers/useAuthChecker';
import api from 'src/helpers/axios';

interface propState {
  _id: string | null;
}

interface Body {
  projectImage?: File;
  title: string | null;
  description: string | null;
}

interface Project {
  _id: string;
  title: string;
  description: string;
  projectImage: File;
  isOnHomePage: number;
  isDeleted: number;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

const fileTypes = ['JPG', 'PNG', 'GIF', 'JPEG'];

const selectedImageStyle: CSSProperties = {
  borderRadius: '10px',
  height: '70px',
  width: '90px',
};
const imageFieldStyle: CSSProperties = { width: '100%', textAlign: 'center' };
const iconStyle: CSSProperties = { fontSize: 48, color: '#333' };
const maxWidth: CSSProperties = { width: '100%' };

const ProjectAdditionForm = () => {
  useAuthChecker();
  const location = useLocation();
  const navigate = useNavigate();
  const { _id } = (location?.state as propState) ?? '';

  const notistack = useSnackbar();
  const [file, setFile] = React.useState<File | null>(null);
  const [title, setTitle] = React.useState<string | null>(null);
  const [shortDescription, setShortDescription] = React.useState<string | null>(null);

  const handleCancel = () => {
    setTitle('');
    setShortDescription('');
    setFile(null);
    navigate('/tables/project');

    // window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  React.useEffect(() => {
    if (_id) {
      const getSingleProjectAPI = async () => {
        const { data } = await axios.get(`${links.baseURL}/api/project/${_id}`);
        if (data) {
          console.log({ data });
          const { project }: { project: Project } = data;

          const { title, projectImage, description } = project;

          setTitle(title);
          setShortDescription(description);
          setFile(projectImage);
        }
      };

      getSingleProjectAPI();
    }
  }, [_id]);

  const handleChange = (file: any) => {
    setFile(file);
  };

  const handleSubmit = async () => {
    if (!title || !shortDescription) {
      return notistack.enqueueSnackbar('Please fill out all the fields.', {
        variant: 'warning',
        anchorOrigin: {
          horizontal: 'right',
          vertical: 'bottom',
        },
      });
    }

    const body: Body = {
      title: title,
      description: shortDescription,
    };

    if (file instanceof File) {
      body.projectImage = file;
    }

    if (_id) {
      await formDataRequest(`api/project/${_id}`, body, false, true, (data) => {
        if (data) {
          navigate('/tables/project');
        }
      });
    } else {
      if (!file) {
        return notistack.enqueueSnackbar('Please fill out all the fields.', {
          variant: 'warning',
          anchorOrigin: {
            horizontal: 'right',
            vertical: 'bottom',
          },
        });
      }
      await formDataRequest('api/project', body, true, true, (data) => {
        if (data) {
          navigate('/tables/project');
        }
      });
    }
  };

  const formDataRequest = async (
    url: string,
    data: Body,
    isPostRequest = true,
    showAlert = true,
    cb: (args?: any) => void,
  ) => {
    const method = isPostRequest ? 'post' : 'put';
    try {
      const response = await api[method](url, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      if (response.status >= 400) {
        cb && cb(false);
        if (showAlert) {
          notistack.enqueueSnackbar('Error uploading your file', {
            variant: 'error',
            anchorOrigin: {
              horizontal: 'right',
              vertical: 'bottom',
            },
          });
        }
      } else {
        cb && cb(true);
        if (showAlert) {
          notistack.enqueueSnackbar('Process completed!', {
            variant: 'success',
            anchorOrigin: {
              horizontal: 'right',
              vertical: 'bottom',
            },
          });

          return response;
        }
      }
    } catch (error) {
      cb && cb(false);
      notistack.enqueueSnackbar('Error uploading your project', {
        variant: 'error',
        anchorOrigin: {
          horizontal: 'right',
          vertical: 'bottom',
        },
      });
      console.log(error);

      return error;
    }
  };

  // if (loadingItems)
  //   return (
  //     <ParentCard title="Loading...">
  //       <>
  //         <CustomFormLabel htmlFor="product-title">
  //           Fetching Brands and Categories...
  //         </CustomFormLabel>
  //         <Spinner />
  //       </>
  //     </ParentCard>
  //   );
  // else

  return (
    <ParentCard title={`Project ${_id ? 'Update' : 'Addition'} Form`}>
      <form>
        <CustomFormLabel htmlFor="project-name">Title</CustomFormLabel>
        <CustomTextField
          onChange={(e: any) => setTitle(e.target.value)}
          value={title}
          id="project-name"
          type="text"
          variant="outlined"
          fullWidth
        />

        <CustomFormLabel htmlFor="desc"> Desc</CustomFormLabel>
        <CustomTextField
          onChange={(e: any) => setShortDescription(e.target.value)}
          value={shortDescription}
          id="desc"
          type="text"
          variant="outlined"
          fullWidth
        />

        <Box mt={2} />

        {_id &&
          (typeof file === 'string' ? (
            <>
              <CustomFormLabel htmlFor="alt-image">Current Project Image</CustomFormLabel>
              <div style={maxWidth}>
                <img src={links.uploads + file} alt={'a'} style={selectedImageStyle} />
              </div>
            </>
          ) : null)}

        <FileUploader handleChange={handleChange} name="file" types={fileTypes}>
          <div className="custom-dropzone" style={maxWidth}>
            {file !== null && file instanceof File ? (
              <>
                <div style={maxWidth}>
                  <img src={URL.createObjectURL(file)} alt={'a'} style={selectedImageStyle} />
                </div>
                <Button
                  onClick={() => {
                    setFile(null);
                  }}
                  variant="outlined"
                >
                  Clear
                </Button>
              </>
            ) : (
              <div>
                <CloudUploadIcon style={iconStyle} />
                <p style={imageFieldStyle}>{_id && 'Replace '} Project</p>
              </div>
            )}
          </div>
        </FileUploader>

        <Box mt={2} />

        <div>
          <Button onClick={() => handleSubmit()} color="primary" variant="contained">
            {_id ? 'Update' : 'Submit'}
          </Button>
          {_id && (
            <Button sx={{ ml: 2 }} onClick={() => handleCancel()} color="warning" variant="text">
              Cancel
            </Button>
          )}
        </div>
      </form>
    </ParentCard>
  );
};

export default ProjectAdditionForm;
